import { useState, useCallback, useEffect } from 'react';

export interface LearningProgress {
  currentStep: number;
  completedCards: string[];
  quizCompleted: boolean;
  quizCorrect: boolean;
}

const DEFAULT_PROGRESS: LearningProgress = {
  currentStep: 0,
  completedCards: [],
  quizCompleted: false,
  quizCorrect: false
};

function getStorageKey(slug: string) {
  return `twygs-learning-${slug}`;
}

function loadProgress(slug: string): LearningProgress {
  try {
    const raw = localStorage.getItem(getStorageKey(slug));
    if (!raw) return DEFAULT_PROGRESS;
    const parsed = JSON.parse(raw);
    return {
      currentStep: parsed.currentStep ?? 0,
      completedCards: Array.isArray(parsed.completedCards) ? parsed.completedCards : [],
      quizCompleted: !!parsed.quizCompleted,
      quizCorrect: !!parsed.quizCorrect
    };
  } catch {
    return DEFAULT_PROGRESS;
  }
}

function saveProgress(slug: string, progress: LearningProgress) {
  try {
    localStorage.setItem(getStorageKey(slug), JSON.stringify(progress));
  } catch {
    // localStorage full or unavailable
  }
}

export function useLearningProgress(slug: string | undefined) {
  const [progress, setProgress] = useState<LearningProgress>(() =>
    slug ? loadProgress(slug) : DEFAULT_PROGRESS
  );

  useEffect(() => {
    if (slug) setProgress(loadProgress(slug));
  }, [slug]);

  const persist = useCallback(
    (next: LearningProgress) => {
      setProgress(next);
      if (slug) saveProgress(slug, next);
    },
    [slug]
  );

  const markCardComplete = useCallback(
    (cardId: string) => {
      persist({
        ...progress,
        completedCards: progress.completedCards.includes(cardId)
          ? progress.completedCards
          : [...progress.completedCards, cardId]
      });
    },
    [progress, persist]
  );

  const setStep = useCallback(
    (step: number) => {
      persist({ ...progress, currentStep: step });
    },
    [progress, persist]
  );

  const completeQuiz = useCallback(
    (correct: boolean) => {
      persist({
        ...progress,
        quizCompleted: true,
        quizCorrect: correct
      });
    },
    [progress, persist]
  );

  const reset = useCallback(() => {
    persist(DEFAULT_PROGRESS);
  }, [persist]);

  const isComplete = progress.quizCompleted;
  const hasStarted = progress.completedCards.length > 0 || progress.currentStep > 0;

  return {
    progress,
    markCardComplete,
    setStep,
    completeQuiz,
    reset,
    isComplete,
    hasStarted
  };
}
